import React from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {FiPause, FiPlay} from 'react-icons/fi';
import {getLanguageFlag} from '../i18n/languageFlags';
import {SPOKEN_LANGUAGES} from '../i18n/spokenLanguages';

export function speechLanguageName(language, locale) {
    try { return new Intl.DisplayNames([locale || 'en'], {type: 'language'}).of(language); }
    catch { return SPOKEN_LANGUAGES.find(item => item.value === language.split('-')[0])?.label || language; }
}

export default function ServerTtsVoicePicker({voices = [], voice, onVoiceChange, onPreviewToggle,
    playing = false, disabled = false, loading = false, error = false}) {
    const {t: lnT, i18n} = useTranslation();
    if (loading) return <Hint role="status">{lnT('common:loading')}</Hint>;
    if (error || !voices.length) return <Hint role="alert">{lnT('messages:serverVoicesUnavailable')}</Hint>;
    return <Tags role="group" aria-label={lnT('messages:voice')}>
        {voices.map(option => {
            const active = voice === option.id;
            const language = speechLanguageName(option.language, i18n.language);
            return <Tag key={option.id} type="button" disabled={disabled}
                title={`${option.name} · ${language}`} aria-label={option.name} aria-description={language}
                aria-pressed={active} $playing={active && playing} onClick={() => active ? onPreviewToggle() : onVoiceChange(option.id)}>
                <IconSlot aria-hidden="true" $playing={active && playing}>
                    <span className="voice-flag">{getLanguageFlag(option.language.split('-')[0])}</span>
                    <FiPlay className="voice-play" size={15}/>
                    <FiPause className="voice-pause" size={15}/>
                </IconSlot>
                {option.name}
            </Tag>;
        })}
    </Tags>;
}
const Tags = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const IconSlot = styled.span`
    width: 18px; height: 18px; position: relative; flex-shrink: 0;
    display: inline-grid; place-items: center;
    > * {grid-area: 1 / 1; transition: opacity 100ms ease;}
    .voice-flag {font-size: 15px; line-height: 18px; opacity: ${({$playing}) => $playing ? 0 : 1};}
    .voice-play {opacity: 0;}
    .voice-pause {opacity: ${({$playing}) => $playing ? 1 : 0};}
`;
const Tag = styled.button`
    display: inline-flex; gap: 8px; align-items: center; padding: 9px 14px;
    min-height: 40px; border: 1px solid rgba(255,255,255,.16); border-radius: 999px;
    cursor: pointer; font: inherit; font-size: 14px; font-weight: 500;
    background: ${({ 'aria-pressed': selected }) => selected ? '#f3f3ed' : 'rgba(255,255,255,.035)'};
    color: ${({ 'aria-pressed': selected }) => selected ? '#222729' : 'rgba(255,255,255,.88)'};
    transition: background 120ms ease, border-color 120ms ease;
    &:not(:disabled):hover {border-color: rgba(255,255,255,.4);}
    &:focus-visible {outline: 2px solid #b6c9bc; outline-offset: 3px;}
    &:focus-visible ${IconSlot} .voice-flag {opacity: 0;}
    &:focus-visible ${IconSlot} .voice-play {opacity: ${({$playing}) => $playing ? 0 : 1};}
    @media (hover: hover) {
        &:not(:disabled):hover ${IconSlot} .voice-flag {opacity: 0;}
        &:not(:disabled):hover ${IconSlot} .voice-play {opacity: ${({$playing}) => $playing ? 0 : 1};}
    }
    &:disabled {opacity: .45; cursor: default;}
`;
const Hint = styled.p`margin: 0; font-size: 12px; line-height: 1.5; color: rgba(255,255,255,.6);`;
